<script setup>
import FormButton from '@/components/Atoms/FormButton.vue'
import FormSwitch from '@/components/Atoms/FormSwitch.vue'
import Tooltip from '@/components/Atoms/Tooltip.vue'
import PermissionTooltip from '@/components/Moleculas/PermissionTooltip.vue'
import IconBase from '@/components/icon/IconBase.vue'
import IconPlus from '@/components/icon/IconPlus.vue'
import { showModal } from '@/Utils/modalBus'

const props = defineProps({
	streamType: {
		type: String,
		required: true,
	},
	platformUsage: {
		type: Object,
		default: () => ({}),
	},
	showToggleAll: {
		type: Boolean,
		default: false,
	},
	guestDestinationEnabled: {
		type: Boolean,
		default: false,
	},
	cannotTogglePlatforms: {
		type: Boolean,
		default: false,
	},
	userEmail: {
		type: String,
		default: '',
	},
	groupToggleState: {
		type: Boolean,
		default: false,
	},
})

const emit = defineEmits(['update:groupToggleState', 'toggle-all', 'new-group', 'invite-guest'])

const addPlatform = () => {
	if (props.streamType === 'subsecond') {
		showModal('modal-add-castr-stream')
		return
	}
	showModal('modal-add-platform')
}

function onToggleAll(value) {
	emit('update:groupToggleState', value)
	emit('toggle-all', value)
}
</script>

<template>
	<div>
		<div v-if="streamType === 'scheduled'" class="flex items-center gap-x-2 mb-3">
			<h3 class="text-base font-medium m-0">Destination</h3>
			<span class="text-tiny font-medium bg-surface-3 text-surface-8 rounded-full px-2 py-0.5">
				{{ platformUsage.usedDestination || 0 }}/{{ platformUsage.totalDestination || 0 }}
			</span>
		</div>
		<div class="flex flex-col gap-2 mb-2 md:mb-3 md:flex-row md:flex-wrap md:items-center">
			<div class="flex flex-wrap items-center gap-2 grow">
				<FormButton v-if="streamType === 'restream'" size="md" @click="addPlatform">
					<icon-base class="mr-2" fill="none">
						<icon-plus />
					</icon-base>
					Add Platform
				</FormButton>
				<FormButton
					v-if="streamType === 'scheduled' || streamType === 'live'"
					size="md"
					:type="streamType === 'scheduled' ? 'secondary' : 'main'"
					class="items-center justify-center"
					@click="addPlatform"
				>
					<icon-base class="mr-2" fill="none">
						<icon-plus />
					</icon-base>
					Add destination
				</FormButton>
				<FormButton v-if="streamType === 'subsecond'" size="md" @click="addPlatform">
					<icon-base class="" fill="none">
						<icon-plus />
					</icon-base>
					Add Stream within Castr
				</FormButton>

				<div v-if="streamType === 'live'" class="flex items-center gap-2 lg:ml-2">
					<Tooltip :active="true" content="Combine multiple destinations into a group">
						<FormButton size="md" class="items-center justify-center" type="secondary" @click="emit('new-group')">
							<img src="@/assets/icons/groupDes.svg" class="md:mr-2" />
							<span class="hidden md:inline">New group</span>
						</FormButton>
					</Tooltip>
					<Tooltip
						:active="true"
						:content="guestDestinationEnabled ? 'Invite a guest to add their destination' : 'You do not have access to Guest Destinations feature'"
					>
						<div class="relative inline-block">
							<FormButton
								size="md"
								class="items-center justify-center relative z-[1]"
								type="secondary"
								:disabled="!guestDestinationEnabled"
								@click="emit('invite-guest')"
							>
								<img src="@/assets/icons/guestDes.svg" class="md:mr-2" />
								<span class="hidden md:inline">Invite guest</span>
							</FormButton>
						</div>
					</Tooltip>
				</div>
			</div>
			<div v-if="showToggleAll" class="flex w-full items-center justify-start mt-1 md:mt-0 md:ml-auto md:w-auto md:justify-end">
				<PermissionTooltip
					:email="userEmail"
					:active="cannotTogglePlatforms"
					wrapper-class="inline-flex items-center"
				>
					<FormSwitch
						label-styles="text-sm text-surface-9 md:flex-row-reverse"
						:disabled="cannotTogglePlatforms"
						:model-value="groupToggleState"
						@update:model-value="onToggleAll"
					>
						Toggle All
					</FormSwitch>
				</PermissionTooltip>
			</div>
		</div>
	</div>
</template>
